import { useSubscription } from "../../hooks/useSubscription";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X, CreditCard } from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Subscription warning banner component
 * Displays warnings for expiring subscriptions, expired subscriptions, and low credits
 */
const SubscriptionWarning = () => {
	const {
		isPremium,
		isExpired,
		isExpiringSoon,
		hasLowCredits,
		daysUntilExpiration,
		credits,
	} = useSubscription();
	const navigate = useNavigate();
	const [dismissed, setDismissed] = useState(false);

	// Don't show if not premium or dismissed
	if (!isPremium || dismissed) return null;

	// Priority: expired > expiring soon > low credits
	const showExpired = isExpired;
	const showExpiringSoon = isExpiringSoon && !isExpired;
	const showLowCredits = hasLowCredits && !isExpired && !isExpiringSoon;

	if (!showExpired && !showExpiringSoon && !showLowCredits) return null;

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0, y: -20 }}
				className={`w-full ${
					showExpired
						? "bg-red-600"
						: showExpiringSoon
						? "bg-amber-500"
						: "bg-blue-600"
				} text-white shadow-lg`}
			>
				<div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
					<div className="flex items-center gap-3">
						<AlertTriangle className="w-5 h-5 flex-shrink-0" />
						<div>
							{showExpired && (
								<p className="font-semibold">
									Your subscription has expired. Renew now to continue using premium features.
								</p>
							)}
							{showExpiringSoon && (
								<p className="font-semibold">
									Your subscription expires in {daysUntilExpiration} day
									{daysUntilExpiration !== 1 ? "s" : ""}. Renew now to avoid interruption.
								</p>
							)}
							{showLowCredits && (
								<p className="font-semibold">
									Low credits remaining: {credits} credits left. Consider purchasing more credits.
								</p>
							)}
						</div>
					</div>
					<div className="flex items-center gap-3">
						<button
							onClick={() => navigate("/plans")}
							className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
						>
							<CreditCard className="w-4 h-4" />
							{showExpired || showExpiringSoon ? "Renew Now" : "Buy Credits"}
						</button>
						<button
							onClick={() => setDismissed(true)}
							className="p-2 hover:bg-white/20 rounded-lg transition-colors"
							aria-label="Dismiss"
						>
							<X className="w-5 h-5" />
						</button>
					</div>
				</div>
			</motion.div>
		</AnimatePresence>
	);
};

export default SubscriptionWarning;

